import React, { useEffect, useMemo, useRef, useState } from 'react';
import SEO from '../components/SEO';
import GlowCard from '../components/GlowCard';
import { Link, useLocation, useNavigate } from 'react-router';
import { SITE_NAME, SITE_URL } from '../constants';

type NewsItem = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  category: string;
  featuredImage?: string;
  publishedAt: string | null;
};

type NewsResponse = {
  items: NewsItem[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

const getPageFromSearch = (search: string) => {
  const params = new URLSearchParams(search);
  const rawPage = Number.parseInt(params.get('page') || '1', 10);
  return Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;
};

const isRemoteUrl = (value?: string | null) => /^https?:\/\//i.test(value || '');

const buildImageUrl = (src?: string | null) => {
  if (!src) return '';
  if (isRemoteUrl(src)) return src;
  return `/image?src=${encodeURIComponent(src)}&w=960`;
};

const NewsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialPage = getPageFromSearch(location.search);
  const bootstrap =
    typeof window !== 'undefined'
      ? ((window as typeof window & { __NEWS_BOOTSTRAP__?: NewsResponse }).__NEWS_BOOTSTRAP__ as NewsResponse | undefined)
      : ((globalThis as typeof globalThis & { __SSR_NEWS_BOOTSTRAP__?: NewsResponse }).__SSR_NEWS_BOOTSTRAP__ as NewsResponse | undefined);
  const bootstrapMatchesPage = bootstrap?.page === initialPage;
  const bootstrapRef = useRef(bootstrapMatchesPage ? bootstrap : undefined);
  const [items, setItems] = useState<NewsItem[]>(bootstrapMatchesPage ? bootstrap.items : []);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(!bootstrapMatchesPage);
  const [page, setPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(bootstrapMatchesPage ? bootstrap.totalPages : 1);

  useEffect(() => {
    const nextPage = getPageFromSearch(location.search);
    setPage((currentPage) => (currentPage === nextPage ? currentPage : nextPage));
  }, [location.search]);

  useEffect(() => {
    let isMounted = true;
    const loadNews = async () => {
      setIsLoading(true);
      setError(null);
      try {
        if (bootstrapRef.current && page === bootstrapRef.current.page) {
          if (typeof window !== 'undefined') delete (window as any).__NEWS_BOOTSTRAP__;
          setItems(bootstrapRef.current.items);
          setTotalPages(bootstrapRef.current.totalPages);
          setIsLoading(false);
          bootstrapRef.current = undefined;
          return;
        }
        const response = await fetch(`/api/news?page=${page}&limit=9`);
        if (!response.ok) throw new Error('Request failed');
        const payload = await response.json();
        if (isMounted) {
          setItems(Array.isArray(payload?.items) ? payload.items : []);
          setTotalPages(Number.isFinite(payload?.totalPages) ? payload.totalPages : 1);
        }
      } catch {
        if (isMounted) setError('Unable to load news right now.');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    loadNews();
    return () => {
      isMounted = false;
    };
  }, [page]);

  const formattedItems = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        imageUrl: buildImageUrl(item.featuredImage),
        dateLabel: item.publishedAt
          ? new Date(item.publishedAt).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
          : 'Recent',
      })),
    [items]
  );
  const canonical = page > 1 ? `${SITE_URL}/news?page=${page}` : `${SITE_URL}/news`;
  const schema = useMemo(() => ([
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `News | ${SITE_NAME}`,
      description: 'Company news, product updates, and aviation technology announcements from AviaGov.',
      url: canonical,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: formattedItems.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${SITE_URL}/news/${item.slug}`,
        name: item.title,
      })),
    },
  ]), [canonical, formattedItems]);

  return (
    <div className="animate-fade-in-up">
      <SEO
        title="News"
        description="Company news, product updates, and aviation technology announcements from AviaGov. Non-regulatory marketing news only."
        canonical={canonical}
        ogType="website"
        schema={schema}
      />
      <section className="py-24 px-4 max-w-7xl mx-auto">
        <div className="mb-16 max-w-3xl">
          <div className="text-accent text-xs font-bold uppercase tracking-widest mb-4">Non-regulatory company news</div>
          <h1 className="text-5xl font-bold font-heading mb-4">News</h1>
          <p className="text-slate-500 text-xl">Product updates, company announcements, and aviation technology stories from AviaGov.</p>
        </div>

        {isLoading ? (
          <p className="text-slate-400">Loading news...</p>
        ) : error ? (
          <p className="text-red-400">{error}</p>
        ) : formattedItems.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {formattedItems.map((item, i) => (
              <GlowCard key={item.id || item.slug} delay={i * 100}>
                {item.imageUrl ? <img src={item.imageUrl} alt="" className="w-full h-44 object-cover rounded-2xl mb-6 border border-white/10" loading="lazy" /> : null}
                <div className="text-xs text-accent font-bold mb-4">{item.category || 'News'}</div>
                <h3 className="text-xl font-bold text-white mb-4 leading-tight">{item.title}</h3>
                <p className="text-slate-400 text-sm mb-8">{item.excerpt}</p>
                <div className="flex justify-between items-center mt-auto">
                  <span className="text-slate-500 text-xs">{item.dateLabel}</span>
                  <Link to={`/news/${item.slug}`} className="text-white font-bold text-xs hover:underline">Read News &rarr;</Link>
                </div>
              </GlowCard>
            ))}
          </div>
        ) : (
          <p className="text-slate-400">No news has been published yet.</p>
        )}

        {!isLoading && !error && totalPages > 1 ? (
          <div className="mt-12 flex items-center justify-center gap-4 text-sm">
            <button type="button" onClick={() => navigate(`/news?page=${Math.max(1, page - 1)}`)} disabled={page <= 1} className="px-4 py-2 rounded-full border border-white/10 text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:border-accent transition-colors">Previous</button>
            <span className="text-slate-500">Page {page} of {totalPages}</span>
            <button type="button" onClick={() => navigate(`/news?page=${Math.min(totalPages, page + 1)}`)} disabled={page >= totalPages} className="px-4 py-2 rounded-full border border-white/10 text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:border-accent transition-colors">Next</button>
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default NewsPage;
